<template>
  <div class="question-screen">
    <ProgressHeader
      :current="current"
      :total="total"
      tone="green"
      @close="$emit('close')"
    />

    <CircularTimer
      :remaining="remaining"
      :progress="progress"
    />

    <section class="question-card question-card--cyan question-card--main">
      <ProblemVisual
        v-if="question.imageSrc || question.visualType"
        :visual-type="question.imageSrc ? 'none' : question.visualType"
        :image-src="question.imageSrc"
        :image-alt="question.imageAlt"
      />

      <h2
        v-if="question.prompt"
        class="question-card__prompt"
      >
        {{ question.prompt }}
      </h2>
    </section>

    <section
      v-if="phase === 'think'"
      class="instruction-card"
    >
      <p>{{ question.instruction }}</p>

      <button
        class="ghost-button"
        type="button"
        @click="finishThinking"
      >
        생각 완료
      </button>
    </section>

    <template v-else>
      <ErrorSolutionBlock
        :visual-type="question.visualType"
      />

      <div class="choice-grid choice-grid--error">
        <ChoiceButton
          v-for="choice in question.choices"
          :key="choice.id"
          :choice="choice"
          :selected="selectedChoiceId === choice.id"
          :disabled="locked"
          compact
          @select="select(choice.id, true)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CircularTimer from '@/components/CircularTimer.vue'
import ChoiceButton from '@/components/ChoiceButton.vue'
import ErrorSolutionBlock from '@/components/ErrorSolutionBlock.vue'
import ProblemVisual from '@/components/ProblemVisual.vue'
import ProgressHeader from '@/components/ProgressHeader.vue'
import { useCountdown } from '@/composables/useCountdown'
import type { Question } from '@/types/content'

const props = defineProps<{
  question: Question
  current: number
  total: number
}>()

const emit = defineEmits<{
  answered: [
    payload: {
      choiceId: string
      selectedByUser: boolean
      elapsedMs: number
    },
  ]
  close: []
}>()

const phase = ref<'think' | 'answer'>('think')
const selectedChoiceId = ref('')
const locked = ref(false)
const active = ref(true)

const duration = computed(() =>
  phase.value === 'think'
    ? props.question.thinkSeconds ?? props.question.timerSeconds
    : props.question.answerSeconds ?? props.question.timerSeconds,
)

const {
  remaining,
  progress,
  stop,
  reset,
  elapsedMs,
} = useCountdown(
  duration,
  active,
  handleTimeout,
)

function finishThinking(): void {
  if (phase.value !== 'think') return

  phase.value = 'answer'
  active.value = true
  reset()
}

function timeoutChoiceId(): string {
  if (props.question.timeoutChoiceId === '__last__') {
    return props.question.choices.at(-1)?.id ?? ''
  }

  return props.question.timeoutChoiceId
}

function handleTimeout(): void {
  if (phase.value === 'think') {
    finishThinking()
    return
  }

  select(timeoutChoiceId(), false)
}

function select(
  choiceId: string,
  selectedByUser: boolean,
): void {
  if (locked.value || !choiceId) return

  locked.value = true
  active.value = false
  stop()

  selectedChoiceId.value = choiceId

  const elapsed = selectedByUser
    ? elapsedMs()
    : duration.value * 1000

  window.setTimeout(() => {
    emit('answered', {
      choiceId,
      selectedByUser,
      elapsedMs: elapsed,
    })
  }, 450)
}

watch(
  () => props.question.id,
  () => {
    phase.value = 'think'
    selectedChoiceId.value = ''
    locked.value = false
    active.value = true
    reset()
  },
)
</script>